<script>
import moment from 'moment-timezone';
import isMeetingFinished, { MEETING_LEAD_MS } from '@shared/utils/meeting';
import { formatCalendarEventDates } from '@shared/utils/calendar';

const LOCATION_ICONS = {
  talkizy: 'video',
  visio: 'video',
  link: 'external-link-alt',
  address: 'map-marker-alt',
};

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      now: Date.now(),
      nowTimer: null,
    };
  },
  mounted() {
    this.nowTimer = setInterval(() => {
      this.now = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    if (this.nowTimer) {
      clearInterval(this.nowTimer);
    }
  },
  computed: {
    isTrainingItem() {
      return this.item.type === 'training_item';
    },
    // Classe virtuelle Talkizy : location_value porte le lien de connexion perso
    // de l'apprenant (injecté côté back). Pas de statut live ici → gating sur les dates.
    isVisio() {
      return this.item.location_type === 'talkizy';
    },
    joinUrl() {
      return this.isVisio ? this.item.location_value : null;
    },
    meetingSource() {
      return {
        starts_at: this.item.start_date ? moment.utc(this.item.start_date).toISOString() : null,
        ends_at: this.item.end_date ? moment.utc(this.item.end_date).toISOString() : null,
      };
    },
    isMeetingOver() {
      return isMeetingFinished(this.meetingSource, this.now);
    },
    // Joignable dès quelques minutes avant le début, tant que ce n'est pas terminé.
    canJoin() {
      if (isMeetingFinished(this.meetingSource, this.now)) return false;
      if (!this.meetingSource.starts_at) return false;
      return this.now >= new Date(this.meetingSource.starts_at).getTime() - MEETING_LEAD_MS;
    },
    // Fuseau de l'événement (défini par le formateur / la classe virtuelle) :
    // on affiche l'heure DANS ce fuseau, comme la vue calendrier côté formateur,
    // plutôt que dans le fuseau du navigateur — sinon le libellé de fuseau serait
    // incohérent avec l'heure montrée.
    eventTimezone() {
      return this.item.timezone || 'Europe/Paris';
    },
    localStart() {
      return moment.utc(this.item.start_date).tz(this.eventTimezone);
    },
    localEnd() {
      return this.item.end_date ? moment.utc(this.item.end_date).tz(this.eventTimezone) : null;
    },
    timezoneLabel() {
      const offset = moment.tz(this.item.start_date || new Date(), this.eventTimezone).format('Z');
      return `${this.eventTimezone.replace(/_/g, ' ')} (UTC${offset})`;
    },
    dateSquareMonth() {
      return this.localStart.format('MMM').replace('.', '');
    },
    dateSquareDay() {
      return this.localStart.format('D');
    },
    // Événement sur plusieurs jours : la date de fin tombe un autre jour que la
    // date de début.
    isMultiDay() {
      return !!this.localEnd && !this.localStart.isSame(this.localEnd, 'day');
    },
    timeLabel() {
      // Plusieurs jours : on affiche l'intervalle complet pour que l'apprenant
      // voie que l'événement s'étale sur plusieurs jours (sinon on ne le sait pas).
      if (this.isMultiDay) {
        const fmt = this.item.is_all_day ? 'DD MMM YYYY' : 'DD MMM YYYY, HH:mm';
        return this.$t('date_range', {
          start: this.localStart.format(fmt),
          end: this.localEnd.format(fmt),
        });
      }

      if (this.item.is_all_day) return this.$t('all_day');

      const start = this.localStart.format('HH:mm');

      if (this.localEnd) {
        return `${start} - ${this.localEnd.format('HH:mm')}`;
      }
      return start;
    },
    unlockLabel() {
      return this.$t('unlocks_on', { date: formatCalendarEventDates(this.item) });
    },
    locationIcon() {
      return LOCATION_ICONS[this.item.location_type] || 'map-marker-alt';
    },
  },
  i18n: {
    messages: {
      en: {
        all_day: 'All day',
        date_range: 'From {start} to {end}',
        unlocks_on: 'This content will be unlocked on {date}',
        join_meeting: 'Join',
        join_hint: 'This button will be available a few minutes before the session starts',
      },
      fr: {
        all_day: 'Toute la journée',
        date_range: 'Du {start} au {end}',
        unlocks_on: 'Contenu débloqué le {date}',
        join_meeting: 'Rejoindre',
        join_hint: 'Ce bouton sera disponible quelques minutes avant le début de la visio',
      },
      es: {
        all_day: 'Todo el día',
        date_range: 'Del {start} al {end}',
        unlocks_on: 'Este contenido se desbloqueará el {date}',
        join_meeting: 'Unirse',
        join_hint: 'Este botón estará disponible unos minutos antes del inicio de la sesión',
      },
    },
  },
};
</script>

<template>
  <div class="compcic box is-clickable p-4 mb-3" @click="$emit('click', item)">
    <div class="is-flex is-align-items-flex-start">
      <div class="compcic_date has-text-centered mr-4 bradius-8">
        <p class="compcic_date_month is-size-9 is-uppercase has-text-weight-medium has-text-primary is-custom">
          {{ dateSquareMonth }}
        </p>
        <p class="compcic_date_day is-size-5 has-text-weight-bold">
          {{ dateSquareDay }}
        </p>
      </div>

      <div class="compcic_body is-flex-grow-1">
        <h3 class="title is-5 mb-1">
          {{ item.title }}
        </h3>

        <p v-if="!isTrainingItem" class="is-size-8 has-text-grey-dark">
          {{ timeLabel }}
          <template v-if="!item.is_all_day">
            <span class="mx-1">·</span>
            <b-icon icon="globe" size="is-small" class="mr-1" />
            {{ timezoneLabel }}
          </template>
        </p>

        <template v-if="isTrainingItem">
          <p class="compcic_meta is-size-8 has-text-grey-dark">
            <b-icon icon="unlock-alt" size="is-small" class="mr-1" />
            {{ unlockLabel }}
          </p>
          <p v-if="item.training_name" class="compcic_meta is-size-8 has-text-grey-dark">
            <b-icon icon="book" size="is-small" class="mr-1" />
            {{ item.training_name }}
          </p>
        </template>

        <p v-else-if="item.location_type && !isVisio" class="compcic_meta is-size-8 has-text-grey-dark">
          <b-icon :icon="locationIcon" size="is-small" class="mr-1" />
          {{ item.location_value }}
        </p>
      </div>

      <figure v-if="item.picture" class="compcic_thumb ml-4">
        <img :src="item.picture" alt="" class="bradius-8">
      </figure>

      <div
        v-if="isVisio && joinUrl && !isMeetingOver"
        class="compcic_join ml-4 is-align-self-center"
        @click.stop
      >
        <a
          v-if="canJoin"
          :href="joinUrl"
          target="_blank"
          rel="noopener"
        >
          <b-button type="is-primary is-custom" size="is-small">
            {{ $t('join_meeting') }}
          </b-button>
        </a>
        <b-tooltip
          v-else
          :label="$t('join_hint')"
          position="is-left"
          multilined
        >
          <b-button type="is-primary is-custom" size="is-small" disabled>
            {{ $t('join_meeting') }}
          </b-button>
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.compcic {
  &_date {
    min-width: 56px;
    padding: 6px 8px;
    border: 1px solid $theme_color_grey_light;
    align-self: flex-start;

    &_month {
      line-height: 1.1;
    }

    &_day {
      line-height: 1.2;
      color: $theme_color_black;
    }
  }

  &_body {
    min-width: 0;
  }

  &_meta {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &_thumb {
    width: 64px;
    height: 64px;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}
</style>
